import type { AccountMailboxFolder } from '../useData';

const STORAGE_KEY = 'ums-email-sidebar-selection-v1';

export type EmailSpecialView = 'drafts' | 'archived' | 'templates' | 'outbox' | 'mySent';

const MAILBOX_FOLDERS = new Set<AccountMailboxFolder>(['INBOX', 'DRAFT', 'SENT', 'MY_SENT', 'JUNK']);

export type EmailSidebarSelectionStored =
  | { kind: 'all' }
  | { kind: 'mentions' }
  | {
      kind: 'sharedAccount';
      shareId: number;
      accountId?: number;
      folder: AccountMailboxFolder;
      imapPath?: string;
    }
  | { kind: 'searchFolder'; id: number }
  | { kind: 'sharedSearchFolder'; shareId: number }
  | { kind: 'personalFolder'; id: number }
  | { kind: 'sharedFolder'; shareId: number }
  | { kind: 'special'; view: EmailSpecialView };

export function loadEmailSidebarSelection(): EmailSidebarSelectionStored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || !('kind' in parsed)) return null;
    const k = (parsed as { kind: string }).kind;
    if (k === 'all') return { kind: 'all' };
    if (k === 'mentions') return { kind: 'mentions' };
    if (k === 'sharedAccount') {
      const p = parsed as { shareId?: unknown; accountId?: unknown; folder?: unknown; imapPath?: unknown };
      if (typeof p.shareId !== 'number') return null;
      const folder = p.folder;
      if (typeof folder !== 'string' || !MAILBOX_FOLDERS.has(folder as AccountMailboxFolder)) return null;
      const imapPath = typeof p.imapPath === 'string' && p.imapPath.trim() ? p.imapPath : undefined;
      const accountId = typeof p.accountId === 'number' ? p.accountId : undefined;
      return {
        kind: 'sharedAccount',
        shareId: p.shareId,
        folder: folder as AccountMailboxFolder,
        ...(accountId != null ? { accountId } : {}),
        ...(imapPath ? { imapPath } : {}),
      };
    }
    if (k === 'searchFolder') {
      const p = parsed as { id?: unknown };
      if (typeof p.id === 'number') return { kind: 'searchFolder', id: p.id };
    }
    if (k === 'sharedSearchFolder') {
      const p = parsed as { shareId?: unknown };
      if (typeof p.shareId === 'number') return { kind: 'sharedSearchFolder', shareId: p.shareId };
    }
    if (k === 'personalFolder') {
      const p = parsed as { id?: unknown };
      if (typeof p.id === 'number') return { kind: 'personalFolder', id: p.id };
    }
    if (k === 'sharedFolder') {
      const p = parsed as { shareId?: unknown };
      if (typeof p.shareId === 'number') return { kind: 'sharedFolder', shareId: p.shareId };
    }
    if (k === 'special') {
      const view = (parsed as { view?: unknown }).view;
      if (
        view === 'drafts' ||
        view === 'archived' ||
        view === 'templates' ||
        view === 'outbox' ||
        view === 'mySent'
      ) {
        return { kind: 'special', view };
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function saveEmailSidebarSelection(selection: EmailSidebarSelectionStored): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    /**/
  }
}
